import { GAME_TYPES, getHandicapPercent, getHandicapDescription, isMatchPlayGame } from '../../constants/gameTypes'
import { getInitials } from '../../utils/colors'

/**
 * Strokes/handicap configuration step shared between RoundSetupWizard and SessionSetupWizard.
 *
 * Props:
 *   gameTypeId, strokeMode, setStrokeMode,
 *   offLowScope, setOffLowScope,
 *   customHandicapPercent, setCustomHandicapPercent,
 *   customOffLow, setCustomOffLow,
 *   showOffLowScope — true for session wizard (shows matchup vs session scope), false for round wizard
 *
 * Player strokes preview (optional):
 *   playerStrokesMap — { playerId: adjustedStrokes } (session wizard)
 *   scrambleTeamStrokes — { groupIdx: { teamName: { strokes } } } (session wizard, scramble)
 *   groups — matchup groups array (session wizard)
 *   matchPlayers — match_players array
 *   getPlayerByPlayerId — lookup function
 *   playerTeamMap — { playerId: { teamName } } (session wizard)
 *   handicapBreakdown — array from buildGameHandicapBreakdown (round wizard)
 *   teams — teams array (round wizard)
 *   isIndividualGame — boolean (round wizard)
 */
export default function StepStrokes({ gameTypeId, strokeMode, setStrokeMode, offLowScope, setOffLowScope, customHandicapPercent, setCustomHandicapPercent, customOffLow, setCustomOffLow, showOffLowScope = false, playerStrokesMap, scrambleTeamStrokes, groups, matchPlayers, getPlayerByPlayerId, playerTeamMap, handicapBreakdown, teams, isIndividualGame, courseHandicapMap }) {
  const gameType = gameTypeId ? GAME_TYPES.find((g) => g.id === gameTypeId) : null
  const isMatchPlay = isMatchPlayGame(gameTypeId)
  const isScramble = gameTypeId === 'scramble'
  const whsDescription = getHandicapDescription(gameTypeId)

  const handleModeChange = (mode) => {
    setStrokeMode(mode)
    if (mode === 'custom' && customHandicapPercent == null) {
      setCustomHandicapPercent(getHandicapPercent(gameTypeId) ?? 0.85)
      setCustomOffLow(isMatchPlay)
    }
  }

  const radioStyle = (selected, disabled = false) => ({
    padding: '0.75rem',
    backgroundColor: selected ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)' : 'var(--bg-tertiary)',
    borderRadius: '0.5rem',
    border: `1px solid ${selected ? 'var(--accent-primary)' : 'var(--border-color)'}`,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1,
  })

  const radioDot = (selected) => (
    <div style={{
      width: '18px', height: '18px', borderRadius: '50%',
      border: `2px solid ${selected ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      {selected && (
        <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: 'var(--accent-primary)' }} />
      )}
    </div>
  )

  const smallRadioDot = (selected) => (
    <div style={{
      width: '14px', height: '14px', borderRadius: '50%',
      border: `2px solid ${selected ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      {selected && (
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--accent-primary)' }} />
      )}
    </div>
  )

  return (
    <div>
      <h3>Strokes</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '0.75rem' }}>
        Choose how handicap strokes are applied
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {/* WHS Standard */}
        <div onClick={() => handleModeChange('whs')} style={radioStyle(strokeMode === 'whs')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {radioDot(strokeMode === 'whs')}
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>WHS Standard for {gameType?.name || 'Game'}</span>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                {whsDescription}
              </div>
            </div>
          </div>
          {/* Off-low scope sub-option (session wizard only, match play non-scramble) */}
          {strokeMode === 'whs' && showOffLowScope && isMatchPlay && !isScramble && (
            <div style={{ marginTop: '0.625rem', marginLeft: '2.25rem' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.375rem', display: 'block' }}>
                Off-Low Scope
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                {[
                  { id: 'matchup', label: 'Play off the low player in each matchup only' },
                  { id: 'session', label: 'Play off the low player in the entire group' },
                ].map(opt => (
                  <div key={opt.id} onClick={(e) => { e.stopPropagation(); setOffLowScope(opt.id) }}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    {smallRadioDot(offLowScope === opt.id)}
                    <span style={{ fontSize: '0.75rem', color: offLowScope === opt.id ? 'var(--text-primary)' : 'var(--text-muted)' }}>{opt.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Straight Up */}
        <div onClick={() => handleModeChange('straight_up')} style={radioStyle(strokeMode === 'straight_up')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {radioDot(strokeMode === 'straight_up')}
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>Straight Up</span>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                No handicap strokes — gross scores only
              </div>
            </div>
          </div>
        </div>

        {/* Custom */}
        <div onClick={() => handleModeChange('custom')} style={radioStyle(strokeMode === 'custom')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {radioDot(strokeMode === 'custom')}
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>Custom</span>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                Set your own handicap percentage and method
              </div>
            </div>
          </div>
          {/* Custom controls */}
          {strokeMode === 'custom' && (
            <div style={{ marginTop: '0.75rem', marginLeft: '2.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {/* Handicap percentage dropdown */}
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.25rem', display: 'block' }}>
                  Handicap Percentage
                </label>
                <select
                  value={customHandicapPercent ?? 1.0}
                  onChange={(e) => { e.stopPropagation(); setCustomHandicapPercent(Number(e.target.value)) }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '0.8125rem',
                  }}
                >
                  <option value={1.0}>100%</option>
                  <option value={0.95}>95%</option>
                  <option value={0.90}>90%</option>
                  <option value={0.85}>85%</option>
                  <option value={0.80}>80%</option>
                  <option value={0.75}>75%</option>
                </select>
              </div>

              {/* Off-low toggle */}
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.375rem', display: 'block' }}>
                  Handicap Method
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  {[
                    { id: true, label: 'Play off the low player' },
                    { id: false, label: 'Full course handicaps' },
                  ].map(opt => (
                    <div key={String(opt.id)} onClick={(e) => { e.stopPropagation(); setCustomOffLow(opt.id) }}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      {smallRadioDot(customOffLow === opt.id)}
                      <span style={{ fontSize: '0.75rem', color: customOffLow === opt.id ? 'var(--text-primary)' : 'var(--text-muted)' }}>{opt.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Off-low scope (when off-low is on) */}
              {customOffLow && showOffLowScope && (
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.375rem', display: 'block' }}>
                    Off-Low Scope
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                    {[
                      { id: 'matchup', label: 'Off the low player in the matchup only' },
                      { id: 'session', label: 'Off the low player in the entire group' },
                    ].map(opt => (
                      <div key={opt.id} onClick={(e) => { e.stopPropagation(); setOffLowScope(opt.id) }}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        {smallRadioDot(offLowScope === opt.id)}
                        <span style={{ fontSize: '0.75rem', color: offLowScope === opt.id ? 'var(--text-primary)' : 'var(--text-muted)' }}>{opt.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Player Strokes Preview — shows adjusted strokes per player, updates reactively */}
      {strokeMode !== 'straight_up' && matchPlayers?.length > 0 && (groups || teams) && (
        <StrokesPreview
          strokeMode={strokeMode}
          gameTypeId={gameTypeId}
          playerStrokesMap={playerStrokesMap}
          scrambleTeamStrokes={scrambleTeamStrokes}
          groups={groups}
          matchPlayers={matchPlayers}
          getPlayerByPlayerId={getPlayerByPlayerId}
          playerTeamMap={playerTeamMap}
          handicapBreakdown={handicapBreakdown}
          teams={teams}
          isIndividualGame={isIndividualGame}
          courseHandicapMap={courseHandicapMap}
        />
      )}
      {strokeMode === 'straight_up' && matchPlayers?.length > 0 && (groups || teams) && (
        <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'var(--bg-tertiary)', borderRadius: '0.5rem', textAlign: 'center' }}>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            All players receive 0 strokes (gross scores only)
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * Shared player strokes table with C.H. and Strokes columns.
 * Used by both StrokesPreview (Strokes step) and StepConfirm (Confirm step).
 *
 * Props:
 *   groups — matchup groups array (session wizard)
 *   teams — teams array (round wizard)
 *   playerTeamMap — { playerId: { teamName, teamOrder } } for team sub-grouping within matchups
 *   playerStrokesMap — { playerId: adjustedStrokes } (session wizard)
 *   scrambleTeamStrokes — { groupIdx: { teamName: { strokes } } } (session wizard, scramble)
 *   handicapBreakdown — array from buildGameHandicapBreakdown (round wizard)
 *   courseHandicapMap — { playerId: courseHandicap }
 *   getPlayerByPlayerId — lookup function
 *   gameTypeId — game type id
 *   isIndividualGame — boolean (round wizard)
 */
export function PlayerStrokesTable({ groups, teams, playerTeamMap, playerStrokesMap, scrambleTeamStrokes, handicapBreakdown, courseHandicapMap, getPlayerByPlayerId, gameTypeId, isIndividualGame }) {
  const isScramble = gameTypeId === 'scramble'

  const getCH = (pid) => {
    if (courseHandicapMap && courseHandicapMap[pid] != null) return courseHandicapMap[pid]
    const entry = handicapBreakdown?.find(h => h.playerId === pid)
    return entry?.courseHandicap ?? null
  }

  const getStrokes = (pid) => {
    if (playerStrokesMap && playerStrokesMap[pid] != null) return playerStrokesMap[pid]
    const entry = handicapBreakdown?.find(h => h.playerId === pid)
    return entry?.adjustedStrokes ?? null
  }

  const columnHeaderStyle = { fontSize: '0.625rem', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', textAlign: 'right', width: '52px', flexShrink: 0 }

  const PlayerRow = ({ pid }) => {
    const player = getPlayerByPlayerId(pid)
    if (!player) return null
    const ch = getCH(pid)
    const strokes = getStrokes(pid)
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0' }}>
        <div style={{
          width: '20px', height: '20px', borderRadius: '50%', backgroundColor: player.avatar_color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: '700', fontSize: '0.5rem', color: '#fff', flexShrink: 0,
        }}>
          {getInitials(player)}
        </div>
        <span style={{ flex: 1, fontSize: '0.75rem', color: 'var(--text-primary)' }}>
          {player.display_name}
        </span>
        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-muted)', width: '52px', textAlign: 'right', flexShrink: 0, marginRight: '1.25rem' }}>
          {ch != null ? ch : '–'}
        </span>
        <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#5e9be6', width: '52px', textAlign: 'right', flexShrink: 0 }}>
          {strokes != null ? strokes : '–'}
        </span>
      </div>
    )
  }

  const ColumnHeaders = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0 0 0.25rem', marginBottom: '0.125rem' }}>
      <span style={{ width: '20px', flexShrink: 0 }} />
      <span style={{ flex: 1 }} />
      <span style={{ ...columnHeaderStyle, marginRight: '1.25rem' }}>C.H.</span>
      <span style={columnHeaderStyle}>Strokes</span>
    </div>
  )

  // Session wizard path: groups with team sub-grouping
  if (groups) {
    const hasAssignedPlayers = groups.some(g => g.playerIds.length > 0)
    if (!hasAssignedPlayers) return null
    const firstNonEmptyIdx = groups.findIndex(g => g.playerIds.length > 0)

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {groups.map((group, groupIdx) => {
          if (group.playerIds.length === 0) return null

          // Group players by team within matchup
          const byTeam = {}
          group.playerIds.forEach(pid => {
            const teamInfo = playerTeamMap?.[pid]
            const key = teamInfo?.teamName || '_all'
            const order = teamInfo?.teamOrder ?? 0
            if (!byTeam[key]) byTeam[key] = { label: teamInfo?.teamName, order, playerIds: [] }
            byTeam[key].playerIds.push(pid)
          })
          const teamGroups = Object.values(byTeam).sort((a, b) => a.order - b.order)
          const showTeamLabels = teamGroups.length > 1 || (teamGroups.length === 1 && teamGroups[0].label)

          return (
            <div key={groupIdx} style={{ padding: '0.625rem', backgroundColor: 'var(--bg-tertiary)', borderRadius: '0.5rem' }}>
              <div style={{ fontSize: '0.8125rem', fontWeight: '700', marginBottom: '0.375rem' }}>
                {group.name}
              </div>
              {isScramble && scrambleTeamStrokes?.[groupIdx] ? (
                Object.entries(scrambleTeamStrokes[groupIdx]).map(([teamName, info]) => (
                  <div key={teamName} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.25rem 0' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-primary)' }}>{teamName}</span>
                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#5e9be6' }}>
                      {info.strokes} team {info.strokes === 1 ? 'stroke' : 'strokes'}
                    </span>
                  </div>
                ))
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  {groupIdx === firstNonEmptyIdx && <ColumnHeaders />}
                  {teamGroups.map(tg => (
                    <div key={tg.label || '_all'}>
                      {showTeamLabels && (
                        <div style={{
                          fontSize: '0.625rem', fontWeight: '700', color: 'var(--text-muted)',
                          textTransform: 'uppercase', letterSpacing: '0.05em',
                          padding: '0.125rem 0', marginBottom: '0.125rem',
                        }}>
                          {tg.label}
                        </div>
                      )}
                      {tg.playerIds.map(pid => <PlayerRow key={pid} pid={pid} />)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  // Round wizard path: teams
  if (teams) {
    const hasAssignedPlayers = teams.some(t => t.playerIds.length > 0)
    if (!hasAssignedPlayers) return null
    const firstNonEmptyTeamIdx = teams.findIndex(t => t.playerIds.length > 0)

    if (isIndividualGame) {
      return (
        <div style={{ padding: '0.625rem', backgroundColor: 'var(--bg-tertiary)', borderRadius: '0.5rem' }}>
          <ColumnHeaders />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {teams.map(team => {
              const pid = team.playerIds[0]
              if (!pid) return null
              return <PlayerRow key={pid} pid={pid} />
            })}
          </div>
        </div>
      )
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {teams.map((team, teamIdx) => {
          if (team.playerIds.length === 0) return null
          const gameType = GAME_TYPES.find(g => g.id === gameTypeId)
          return (
            <div key={teamIdx} style={{ padding: '0.625rem', backgroundColor: 'var(--bg-tertiary)', borderRadius: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
                <span style={{ fontSize: '0.8125rem', fontWeight: '700' }}>{team.name}</span>
                {gameType?.isScramble && team.playerIds.length >= 2 && (() => {
                  const hcp = handicapBreakdown?.find(h => h.playerId === team.playerIds[0])
                  return hcp ? (
                    <span style={{ fontSize: '0.6875rem', fontWeight: '600', color: '#5e9be6' }}>
                      {hcp.adjustedStrokes} team {hcp.adjustedStrokes === 1 ? 'stroke' : 'strokes'}
                    </span>
                  ) : null
                })()}
              </div>
              {!gameType?.isScramble && (
                <>
                  {teamIdx === firstNonEmptyTeamIdx && <ColumnHeaders />}
                  {team.playerIds.map(pid => <PlayerRow key={pid} pid={pid} />)}
                </>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return null
}

function StrokesPreview(props) {
  const { strokeMode, matchPlayers, groups, teams } = props
  if (!matchPlayers?.length || !(groups || teams)) return null

  return (
    <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
      <h4 style={{ fontSize: '0.8125rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
        Playing Strokes
      </h4>
      <PlayerStrokesTable {...props} />
    </div>
  )
}
